import React from "react";
import { Box, Typography } from "@mui/material";
import SkillSheetMainSummaryMobile from "./SkillSheetMainSummaryTable/SkillSheetMainSummaryMobile";
import SkillSheetMainSummaryDesktop from "./SkillSheetMainSummaryTable/SkillSheetMainSummaryDesktop";
import ActionButton from "../../shared/ActionButton";
import { buildWorkHistoryDisplayValues } from "./SkillSheetMainSummaryTable/buildWorkHistoryDisplayValues";
import ExpandableSection from "../ExpandableSection";
import SkillSheetMainDetailDesktop from "./SkillSheetMainDetail/SkillSheetMainDetailDesktop";
import SkillSheetMainDetailMobile from "./SkillSheetMainDetail/SkillSheetMainDetailMobile";
import {
  PROJECT_COLOR,
  PROJECT_COLOR_DARK,
  GRAY_COLOR,
  GRAY_COLOR_DARK,
} from "../../../constants/colors";
import { FONT_SIZE } from "../../../constants/fontSizes";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";
import ProjectNumberCard from "../../shared/ProjectCardFrame/ProjectNumberCard";
import ProjectNumberLabel from "../../shared/ProjectCardFrame/ProjectNumberLabel";

/** ===== メイン ===== */
export default function SkillSheetMainList({
  workHistoryGroups = [],
  phases = [
    { key: "requirement_definition", label: "要件\n定義" },
    { key: "basic_design", label: "基本\n設計" },
    { key: "detail_design", label: "詳細\n設計" },
    { key: "program", label: "製造\n構築" },
    { key: "test", label: "テス\nト" },
    { key: "maintenance_operation", label: "保守\n運用" },
  ],
  onEdit,
  onDelete,
  showEditButton,
  isPublicPage,
}) {
  const isDesktop = useIsDesktop();

  // 編集・削除ボタンの共通スタイル
  const actionButtonSx = {
    height: 32,
    minWidth: isDesktop ? 96 : 0,
    fontSize: FONT_SIZE.SM,
    boxShadow: "none",
    pr: isDesktop ? 3 : 2,
    pl: isDesktop ? 2.5 : 1.5,
    width: isDesktop ? "auto" : "calc(50% - 4px)",
  };

  if (!workHistoryGroups.length) {
    return (
      <Typography sx={{ color: GRAY_COLOR }}>
        プロジェクトが未登録です
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {workHistoryGroups.map((group, index) => {
        return (
          <ProjectNumberCard key={group.key} index={index + 1}>
            {/* スマホ用の項番 */}
            {!isDesktop && <ProjectNumberLabel index={index + 1} />}

            {group.items.map((workHistory) => {
              const displayValues = buildWorkHistoryDisplayValues(
                workHistory,
                isPublicPage,
              );

              return isPublicPage ? (
                // 公開プロフィール時：
                // 詳細表示は不可にし、一覧のみ表示
                <Box key={workHistory.id}>
                  {isDesktop ? (
                    <SkillSheetMainSummaryDesktop
                      workHistory={workHistory}
                      displayValues={displayValues}
                      phases={phases}
                      isPublicPage={isPublicPage}
                    />
                  ) : (
                    <SkillSheetMainSummaryMobile
                      workHistory={workHistory}
                      displayValues={displayValues}
                      phases={phases}
                      isPublicPage={isPublicPage}
                    />
                  )}
                </Box>
              ) : (
                <ExpandableSection
                  key={workHistory.id}
                  // 一覧（折りたたみ表示）
                  collapsedContent={
                    isDesktop ? (
                      <SkillSheetMainSummaryDesktop
                        workHistory={workHistory}
                        displayValues={displayValues}
                        phases={phases}
                      />
                    ) : (
                      <SkillSheetMainSummaryMobile
                        workHistory={workHistory}
                        displayValues={displayValues}
                        phases={phases}
                      />
                    )
                  }
                  // 詳細（展開時）
                  expandedContent={
                    isDesktop ? (
                      <SkillSheetMainDetailDesktop
                        workSections={displayValues.workSections}
                        skillSections={displayValues.skillSections}
                        displayValues={displayValues}
                      />
                    ) : (
                      <SkillSheetMainDetailMobile
                        workSections={displayValues.workSections}
                        skillSections={displayValues.skillSections}
                        displayValues={displayValues}
                      />
                    )
                  }
                  summaryText="詳細を見る"
                  closeText="詳細を閉じる"
                  expandedSpacing={1}
                  buttonSx={{
                    height: 32,
                    py: 0,
                  }}
                  // ボタンを上部に配置
                  // PC：横並び
                  // スマホ：縦並び（編集/削除 → 詳細）※編集/削除は横並び
                  renderButton={({ buttonUI }) => (
                    <Box sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: isDesktop ? "row" : "column",
                          justifyContent: isDesktop ? "flex-end" : "center",
                          gap: isDesktop ? 1.5 : 1,
                        }}
                      >
                        {/* 詳細ボタン */}
                        <Box
                          sx={{
                            order: isDesktop ? 1 : 2,
                            width: isDesktop ? 170 : "100%",
                          }}
                        >
                          {buttonUI}
                        </Box>

                        {/* 編集・削除ボタン */}
                        {showEditButton && (
                          <Box
                            sx={{
                              order: isDesktop ? 2 : 1,
                              display: "flex",
                              gap: isDesktop ? 1.5 : 1,
                              width: isDesktop ? "auto" : "100%",
                            }}
                          >
                            <ActionButton
                              label="編集する"
                              showArrow
                              onClick={() => onEdit?.(workHistory.id)}
                              sx={{
                                ...actionButtonSx,
                                backgroundColor: PROJECT_COLOR,
                                "&:hover": {
                                  backgroundColor: PROJECT_COLOR_DARK,
                                },
                              }}
                            />

                            <ActionButton
                              label="削除する"
                              showArrow
                              onClick={() => onDelete?.(workHistory.id)}
                              sx={{
                                ...actionButtonSx,
                                backgroundColor: GRAY_COLOR,
                                "&:hover": {
                                  backgroundColor: GRAY_COLOR_DARK,
                                },
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                />
              );
            })}
          </ProjectNumberCard>
        );
      })}
    </Box>
  );
}
